import {
  API_LIEN_WAIVER_ESIGN,
  API_LIEN_WAIVER_ESIGN_LIST,
  API_LIEN_WAIVER_ESIGN_OVERRIDE,
  API_LIEN_WAIVER_ESIGN_SEND,
  API_LIEN_WAIVER_ESIGN_SIGN,
  API_LIEN_WAIVER_ESIGN_VALIDATE,
} from 'config'
import { BorrowerStatus } from 'pages/Borrower/ManageBorrowers/types'
import Api from 'services/api'

import { downloadFile } from '../documents'

const API_BORROWERS = '/borrowers'
const API_BORROWER_BY_ID = '/borrowers/:id'
const API_BORROWER_RESET_PWD = '/borrowers/resetPwd/:id'
const API_BORROWER_LOAN_REQUEST = '/borrowers/requestLoan'
const API_BORROWER_MY_PROFILE = '/borrowers/myProfile'
const API_BORROWER_QUOTE_INFO = '/borrowers/quoteInfo'
const API_BORROWER_CHANGE_STATUS = '/borrowers/changeStatus/:id'

const API_BORROWER_TRACK = '/borrowers/track/:id'
const API_BORROWER_TRACK_BY_ID = '/borrowers/track/:id/:trackId'
const API_BORROWER_FIELD_LOGS = '/borrowers/:id/logs/:fieldName'

const API_DRAW_REQUEST = '/loan/borrower/drawRequest'
const API_DRAW_REQUEST_DECLINE = `${API_DRAW_REQUEST}/:id/decline`
const API_DRAW_REQUEST_WITHDRAW = `${API_DRAW_REQUEST}/:id/withdraw`
const API_DRAW_REQUEST_ADVISORY = `${API_DRAW_REQUEST}/advisory`
const API_DRAW_REQUEST_APPROVE = `${API_DRAW_REQUEST}/:id/approve`
const API_DRAW_REQUEST_APPROVAL_VOID = `${API_DRAW_REQUEST}/:id/approval/void`
const API_DRAW_REQUEST_APPROVAL_PDF = `${API_DRAW_REQUEST}/:id/approval/pdf`
const API_DRAW_REQUEST_APPROVAL_SEND = `${API_DRAW_REQUEST}/:id/approval/sendToBorrower`
const API_DRAW_REQUEST_APPROVAL_VALIDATE = `${API_DRAW_REQUEST}/:id/approval/validate`
const API_DRAW_REQUEST_APPROVAL_DOCUMENT = `${API_DRAW_REQUEST}/:id/approval/document`
const API_DRAW_REQUEST_APPROVAL_ACKNOWLEDGE = `${API_DRAW_REQUEST}/:id/approval/acknowledge`

export const filterBorrowers = (filters: Record<string, any>) => {
  return Api.get(API_BORROWERS, filters)
}

export const resetBorrowerPassword = (id: number) => {
  return Api.post(API_BORROWER_RESET_PWD, {}, { id })
}

export const createDrawRequest = (data: Record<string, any>) => {
  return Api.post(API_DRAW_REQUEST, data)
}

/**
 * What has been ordered on this loan's draws, as the borrower is allowed to see it: the draw
 * inspections and the lien waiver, and none of the servicing flags the staff advisory carries.
 */
export const getBorrowerDrawAdvisory = (scope: { drawSource?: string; drawId?: number } = {}) => {
  return Api.get(API_DRAW_REQUEST_ADVISORY, scope)
}

/** The lender turns a request down. The reason is required: the borrower is shown it. */
export const declineDrawRequest = (id: number, reason: string) => {
  return Api.post(API_DRAW_REQUEST_DECLINE, { reason }, { id })
}

/**
 * The lender's line-by-line decision on a request, raised or revised.
 *
 * Every line the borrower named has to carry an amount, and a line approved for less than was
 * asked has to carry a reason - the backend refuses the lot otherwise, because the borrower is
 * shown this table.
 */
export const approveDrawRequest = (
  id: number,
  lines: { value: string; key: string; amount: number; reason: string }[],
  note: string,
) => {
  return Api.post(API_DRAW_REQUEST_APPROVE, { lines, note }, { id })
}

/**
 * The borrower's copy of the approval, downloaded.
 *
 * The same PDF that is emailed to them, so a servicer reads exactly what the borrower will.
 */
export const downloadDrawApprovalPdf = async (id: number, fileName: string) => {
  const res: Blob = await Api.get(API_DRAW_REQUEST_APPROVAL_PDF, {}, { id }, { responseType: 'blob' })
  downloadFile(fileName, res)
}

/** Email the approval to the borrower for acknowledgement, with the PDF attached. */
export const sendDrawApprovalToBorrower = (id: number, emails: Record<string, string>, note: string) => {
  return Api.post(API_DRAW_REQUEST_APPROVAL_SEND, { emails, note }, { id })
}

/** Whether the link in the borrower's email still belongs to the decision that stands. */
export const validateDrawApprovalSignature = (id: number, urlSignature: string) => {
  return Api.post(API_DRAW_REQUEST_APPROVAL_VALIDATE, { urlSignature }, { id })
}

/** What the borrower's page shows: the approved figures and whether they acknowledged them. */
export const getDrawApprovalDocument = (id: number) => {
  return Api.get(API_DRAW_REQUEST_APPROVAL_DOCUMENT, {}, { id })
}

/** The borrower says they have read the approved amounts. */
export const acknowledgeDrawApproval = (id: number) => {
  return Api.post(API_DRAW_REQUEST_APPROVAL_ACKNOWLEDGE, {}, { id })
}

/** Take an approval back, leaving the request open for a new decision. */
export const voidDrawRequestApproval = (id: number, reason: string) => {
  return Api.post(API_DRAW_REQUEST_APPROVAL_VOID, { reason }, { id })
}

/** The borrower takes their own request back, before anyone has entered a draw for it. */
export const withdrawDrawRequest = (id: number, reason: string) => {
  return Api.post(API_DRAW_REQUEST_WITHDRAW, { reason }, { id })
}

/**
 * Which draw a lien waiver covers. A loan carries one waiver per draw: `request` is a borrower
 * draw request id, `ledger` a trust-ledger draw row id. Sending neither reads the loan-level
 * record, which is what every waiver was before the records were split per draw.
 */
export type LienWaiverScope = { drawSource?: 'request' | 'ledger' | ''; drawId?: number; urlSignature?: string }

export const getLienWaiverEsign = (scope: LienWaiverScope = {}) => {
  return Api.get(API_LIEN_WAIVER_ESIGN, scope)
}

/** Every waiver on the loan, which is what the staff panel lists. */
export const getLienWaiverEsignList = () => {
  return Api.get(API_LIEN_WAIVER_ESIGN_LIST)
}

export const sendLienWaiverEsign = (
  payload: Record<string, any>,
  emails: Record<string, string>,
  note: string,
  scope: LienWaiverScope = {},
) => {
  return Api.post(API_LIEN_WAIVER_ESIGN_SEND, { payload, emails, note, ...scope })
}

export const validateLienWaiverEsign = (data: Record<string, any>) => {
  return Api.post(API_LIEN_WAIVER_ESIGN_VALIDATE, data)
}

/**
 * Sign the waiver this link opened.
 *
 * `urlSignature` is what says which one: the link identifies its own record, so a recipient of one
 * draw's waiver cannot be walked onto another's. `signerName` is who is signing, asked for when
 * the record does not already know the name behind the address.
 */
export const signLienWaiverEsign = (urlSignature = '', signerName = '') => {
  return Api.post(API_LIEN_WAIVER_ESIGN_SIGN, { urlSignature, signerName }, {}, { timeout: 150000 })
}

export const updateLienWaiverEsignOverride = (value: boolean, scope: LienWaiverScope = {}) => {
  return Api.put(API_LIEN_WAIVER_ESIGN_OVERRIDE, { value, ...scope })
}

export const createBorrower = (data: Record<string, any>) => {
  return Api.post(API_BORROWERS, data)
}

export const updateBorrower = (id: number, data: Record<string, any>) => {
  return Api.put(API_BORROWER_BY_ID, data, { id })
}

export const updateBorrowerPassword = (id: number, password: string) => {
  return Api.post(API_BORROWER_BY_ID, { password }, { id })
}

export const getMyBorrowerProfile = () => {
  return Api.get(API_BORROWER_MY_PROFILE)
}

export const updateMyBorrowerProfile = (data: Record<string, any>) => {
  return Api.post(API_BORROWER_MY_PROFILE, data)
}

export const updateMyBorrowerPassword = (password: string) => {
  return Api.put(API_BORROWER_MY_PROFILE, { password })
}

export const updateMyBorrowerMfa = (enabled: boolean) => {
  return Api.post(`${API_BORROWER_MY_PROFILE}/mfa`, { enabled })
}

/** Current text-message consent plus the exact wording the borrower is asked to agree to. */
export const getMyBorrowerSmsConsent = () => {
  return Api.get(`${API_BORROWER_MY_PROFILE}/smsConsent`)
}

export const updateMyBorrowerSmsConsent = (consented: boolean) => {
  return Api.post(`${API_BORROWER_MY_PROFILE}/smsConsent`, { consented })
}

export const getBorrowerLoanRequest = () => {
  return Api.get(API_BORROWER_LOAN_REQUEST)
}

export const updateBorrowerLoanRequest = (data: Record<string, any>) => {
  return Api.put(API_BORROWER_LOAN_REQUEST, data)
}

export const borrowerLoanRequest = (data: Record<string, any>) => {
  return Api.post(API_BORROWER_LOAN_REQUEST, data)
}

export const getBorrowerLink = (id: number) => {
  return Api.get(`${API_BORROWER_BY_ID}/link`, {}, { id })
}

export const sendBorrowerEmail = (id: number) => {
  return Api.post(`${API_BORROWER_BY_ID}/email`, {}, { id })
}

export const getBorrowerLoans = (id: number) => {
  return Api.get(`${API_BORROWER_BY_ID}/loans`, {}, { id })
}

export const getBorrowerByNo = (id: number) => {
  return Api.get(`${API_BORROWER_BY_ID}/no`, {}, { id })
}

export const getBorrowerQuoteInfo = () => {
  return Api.get(API_BORROWER_QUOTE_INFO)
}

export const updateBorrowerStatus = (id: number, status: BorrowerStatus) => {
  return Api.post(API_BORROWER_CHANGE_STATUS, { status }, { id })
}

export const deleteBorrower = (id: number) => {
  return Api.delete(API_BORROWER_BY_ID, {}, { id })
}

export const createBorrowerTrack = (id: number, data: Record<string, any>) => {
  return Api.post(API_BORROWER_TRACK, data, { id })
}

export const uploadBorrowerTracksToLoans = (id: number, trackIds: string[], loans: number[]) => {
  return Api.post(`${API_BORROWER_BY_ID}/uploadTracksToLoans`, { trackIds, loans }, { id })
}

export const updateBorrowerTrack = (id: number, trackId: number, data: Record<string, any>) => {
  return Api.put(API_BORROWER_TRACK_BY_ID, data, { id, trackId })
}

export const deleteBorrowerTrack = (id: number, trackId: number) => {
  return Api.delete(API_BORROWER_TRACK_BY_ID, {}, { id, trackId })
}

export const getBorrowerFieldLogs = (id: number, fieldName: string) => {
  return Api.get(API_BORROWER_FIELD_LOGS, {}, { id, fieldName })
}

// --- Borrower portal: serviced loans, payment history, messages ---
const API_BORROWER_SERVICING = '/borrowers/servicing'
const API_BORROWER_SERVICING_LOANS = `${API_BORROWER_SERVICING}/loans`
const API_BORROWER_SERVICING_LOAN = `${API_BORROWER_SERVICING}/loans/:loanNumber`
const API_BORROWER_SERVICING_PAYOFF_DRAFT = `${API_BORROWER_SERVICING}/loans/:loanNumber/payoffDraft`
const API_BORROWER_SERVICING_PAYOFF_REQUEST = `${API_BORROWER_SERVICING}/loans/:loanNumber/payoffRequest`
const API_BORROWER_SERVICING_PAYMENTS = `${API_BORROWER_SERVICING}/loans/:loanNumber/payments`
const API_BORROWER_SERVICING_TRUST_LEDGER = `${API_BORROWER_SERVICING}/loans/:loanNumber/trustLedger`
const API_BORROWER_SERVICING_PORTAL_PAYMENTS = `${API_BORROWER_SERVICING}/loans/:loanNumber/portalPayments`
const API_BORROWER_SERVICING_PAYMENT_TARGET = `${API_BORROWER_SERVICING}/loans/:loanNumber/paymentTarget`
const API_BORROWER_SERVICING_MESSAGES = `${API_BORROWER_SERVICING}/loans/:loanNumber/messages`
const API_BORROWER_SERVICING_DOCUMENTS = `${API_BORROWER_SERVICING}/loans/:loanNumber/documents`
const API_BORROWER_SERVICING_DOCUMENT_DOWNLOAD = `${API_BORROWER_SERVICING}/loans/:loanNumber/documents/download`
const API_BORROWER_SERVICING_AUTOPAY = `${API_BORROWER_SERVICING}/loans/:loanNumber/autopay`

/** A loan document staff published to the borrower (the "Borrower" toggle in the Documents panel). */
export interface BorrowerServicingDocument {
  key: string
  name: string
  category: string
  sharedAt: number | null
  uploadedByBorrower: boolean
}

export const getBorrowerServicingLoans = (): Promise<{ loans: any[] }> => {
  return Api.get(API_BORROWER_SERVICING_LOANS)
}

export const getBorrowerServicingLoan = (loanNumber: number | string) => {
  return Api.get(API_BORROWER_SERVICING_LOAN, {}, { loanNumber })
}

/** The borrower's own payoff figure, always returned as a watermarked draft. */
export const downloadBorrowerPayoffDraft = (loanNumber: number | string, payoffDate: string) => {
  return Api.post(
    API_BORROWER_SERVICING_PAYOFF_DRAFT,
    { payoffDate },
    { loanNumber },
    { responseType: 'blob', timeout: 150000 },
  )
}

/**
 * Ask the servicer to issue a real payoff statement. Returns a confirmation only - no document
 * comes back, so that asking for an official statement is never mistaken for receiving one. The
 * borrower views a working figure through `downloadBorrowerPayoffDraft` instead.
 */
export const requestBorrowerPayoff = (loanNumber: number | string, payoffDate: string, note: string) => {
  return Api.post(API_BORROWER_SERVICING_PAYOFF_REQUEST, { payoffDate, note }, { loanNumber }, { timeout: 150000 })
}

export const getBorrowerServicingPayments = (loanNumber: number | string): Promise<{ payments: any[] }> => {
  return Api.get(API_BORROWER_SERVICING_PAYMENTS, {}, { loanNumber })
}

export const getBorrowerServicingTrustLedger = (
  loanNumber: number | string,
): Promise<{
  constructionReserve: number | null
  balance: number
  entries: any[]
  servicingSource?: string
  entriesUnavailable?: boolean
}> => {
  return Api.get(API_BORROWER_SERVICING_TRUST_LEDGER, {}, { loanNumber })
}

/**
 * Where this loan's payments are taken. `internal` means our own checkout; `fci` means the loan is
 * being shown from FCI's books, so the borrower pays on FCI's hosted page at `link`.
 */
export const getBorrowerServicingPaymentTarget = (
  loanNumber: number | string,
): Promise<{ servicingSource: string; provider: 'internal' | 'fci'; link: string | null }> => {
  return Api.get(API_BORROWER_SERVICING_PAYMENT_TARGET, {}, { loanNumber })
}

export const getBorrowerServicingPortalPayments = (loanNumber: number | string): Promise<any[]> => {
  return Api.get(API_BORROWER_SERVICING_PORTAL_PAYMENTS, {}, { loanNumber })
}

export const getBorrowerServicingMessages = (loanNumber: number | string): Promise<{ messages: any[] }> => {
  return Api.get(API_BORROWER_SERVICING_MESSAGES, {}, { loanNumber })
}

export const postBorrowerServicingMessage = (loanNumber: number | string, body: string) => {
  return Api.post(API_BORROWER_SERVICING_MESSAGES, { body }, { loanNumber })
}

export const getBorrowerServicingDocuments = (
  loanNumber: number | string,
): Promise<{ documents: BorrowerServicingDocument[] }> => {
  return Api.get(API_BORROWER_SERVICING_DOCUMENTS, {}, { loanNumber })
}

export const downloadBorrowerServicingDocument = (
  loanNumber: number | string,
  key: string,
): Promise<{ url?: string; content?: string }> => {
  return Api.post(API_BORROWER_SERVICING_DOCUMENT_DOWNLOAD, { key }, { loanNumber })
}

/** The loan's AutoPay (recurring ACH), as the portal is allowed to see it: account number last four only. */
export interface BorrowerAutopay {
  enabled: boolean
  canEnroll: boolean
  canUpdateBank: boolean
  authorizationText: string
  enrolled: boolean
  hasBank: boolean
  bank: {
    accountName: string
    accountType: string
    routingNumber: string
    bankName: string
    accountLast4: string
  }
  nextDraftDate: string
  draftAmount: number | null
  /** Why AutoPay was turned off, when a returned draft did it. */
  disabledReason: string
  authorizedAt: string | null
}

export interface BorrowerAutopayUpdate {
  enrolled?: boolean
  bank?: { accountName: string; accountType: string; routingNumber: string; accountNumber: string }
  authorize?: boolean
}

export const getBorrowerAutopay = (loanNumber: number | string): Promise<BorrowerAutopay> => {
  return Api.get(API_BORROWER_SERVICING_AUTOPAY, {}, { loanNumber })
}

export const updateBorrowerAutopay = (
  loanNumber: number | string,
  data: BorrowerAutopayUpdate,
): Promise<BorrowerAutopay> => {
  return Api.put(API_BORROWER_SERVICING_AUTOPAY, data, { loanNumber })
}
